import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { FeedbackService } from '../feedback.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonModule } from '@angular/common';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { CommonService } from '../../../../../shared/services/common.service';
import { AuthService } from '../../../../../core/services/auth.service';
import {
  FeedBackByCase,
  FeedbackDrawerParams,
  FeedbackQuestion,
  QuestionsRes,
} from '../feedback';
import { ApiResponse } from '../../../../../core/models/auth.models';
import { InsightMode } from '../../../insights.enums';
import {
  FeedbackStepAnswer,
  FeedbackStepData,
  StepValidation,
  EnhancedFeedbackQuestion,
  PrintTemplateData,
} from './feedback-step.interface';
import { GeneralBehaviorStepComponent } from './steps/general-behavior-step/general-behavior-step.component';
import { BehaviorMetricsStepComponent } from './steps/behavior-metrics-step/behavior-metrics-step.component';
import { InterventionSupportStepComponent } from './steps/intervention-support-step/intervention-support-step.component';
import { StakeholderFeedbackStepComponent } from './steps/stakeholder-feedback-step/stakeholder-feedback-step.component';
import { SelfAssessmentStepComponent } from './steps/self-assessment-step/self-assessment-step.component';
import { QuestionCategory, SubQuestionCategory } from '../feeback.enum';
import {
  QuestionType,
  QUESTION_TYPE_MAP,
  QUESTION_SCALE_LABELS,
} from './feedback-question.enum';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { OrganizationService } from '../../../../organizations/organization.service';
import { PrintTemplateComponent } from './steps/self-assessment-step/print-template/print-template.component';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../../audit-logs/audit-logs.interface';
import { CaseBehaviour } from '../../../insights.interface';

type FeedbackAuditFormValue = Record<
  string,
  FeedbackStepAnswer[] | string | number | null | undefined
>;

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzSpinModule,
    NzStepsModule,
    GeneralBehaviorStepComponent,
    BehaviorMetricsStepComponent,
    InterventionSupportStepComponent,
    StakeholderFeedbackStepComponent,
    SelfAssessmentStepComponent,
  ],
  templateUrl: './feedback-form.component.html',
  styleUrl: './feedback-form.component.scss',
})
export class FeedbackFormComponent implements OnInit, OnDestroy {
  private readonly drawerService = inject(DrawerService);
  private readonly fb = inject(FormBuilder);
  private readonly loaderService = inject(LoaderService);
  private readonly authService = inject(AuthService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly commonService = inject(CommonService);
  private readonly injector = inject(Injector);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly orgService = inject(OrganizationService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly auditContextService = inject(AuditContextService);

  @ViewChild(SelfAssessmentStepComponent)
  selfAssessmentStepComponent!: SelfAssessmentStepComponent;

  feedbackForm: FormGroup;
  questions: FeedbackQuestion[] = [];
  private readonly destroy$ = new Subject<void>();
  isPrintLoading = false;
  feedbackDetails = {} as FeedbackDrawerParams;
  pendingFeedbackData: FeedBackByCase | null = null;
  overAllScore = {
    GeneralBehaviourRating: 0,
    BehaviorSpecificMetrics: 0,
    InterventionAndSupportFeedback: 0,
    StakeholderFeedback: 0,
    SelfAssessment: 0,
  };
  currentStep = 0;
  totalOverAllScore = 0;
  steps = [
    {
      title: 'General Behavior Rating',
      description: 'Overall behavior assessment',
    },
    {
      title: 'Behavior-Specific Metrics',
      description: 'Detailed behavior analysis',
    },
    {
      title: 'Intervention & Support',
      description: 'Support needs and modifications',
    },
    {
      title: 'Stakeholder Feedback',
      description: 'Progress and parent feedback',
    },
    { title: 'Self-Assessment', description: 'Teacher and student reflection' },
  ];
  stepData: Record<number, FeedbackStepData> = {};
  isStepDataInitialized = false;

  loading$: Observable<boolean> = of(false);
  hasAddedFeedback = false;
  loggedInUserId = 0;
  behaviorMetricsBehaviorIds: number[] = [];
  selectedBehaviorMetricsId: number | null = null;
  notObservedBehaviorIds: number[] = [];
  private behaviorMetricsResponsesByBehaviorId: Record<
    number,
    FeedbackStepAnswer[]
  > = {};

  constructor() {
    this.loading$ = this.loaderService.loading$;
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.loggedInUserId = user?.id || 0;
      });

    this.getQuestions();
    this.feedbackForm = this.fb.group({
      caseFeedbackSubmissionId: [0],
      organizationId: [0, Validators.required],
      caseId: [0, Validators.required],
      caseBehaviorMappingId: [null],
      questionResponses: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.getDrawerParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDrawerParams(): void {
    this.drawerService
      .getParams<FeedbackDrawerParams>(DRAWER_IDS.FEEDBACK_FORM)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.feedbackDetails = params;
          this.hasAddedFeedback =
            params.data?.caseMemberUserId === this.loggedInUserId;
          if (params.data) {
            this.getFeedbackByCaseId();
            this.feedbackForm.patchValue({
              caseFeedbackSubmissionId: params.data.id || 0,
              caseId: params.caseDetail.id || 0,
              organizationId: params.caseDetail.student.organizationId || 0,
            });
          }
          this.initializeBehaviorMetricsState();
        }
      });
  }
  getFeedbackByCaseId(): void {
    const feedbackData = this.extractFeedbackData();
    if (!feedbackData?.questionResponses) {
      return;
    }

    this.processCategories(feedbackData);
    this.totalOverAllScore = feedbackData.overallScore || 0;

    const allQuestionResponses = this.flattenQuestionResponses(feedbackData);
    const mappedResponses = this.mapResponsesToFormFormat(allQuestionResponses);
    this.hydrateBehaviorMetricsStateFromMappedResponses(
      mappedResponses,
      this.getNoObservingBehaviorIdsFromFeedback(feedbackData)
    );

    this.patchFormWithFeedbackData(feedbackData, mappedResponses);
    this.buildQuestionResponsesArray(mappedResponses);
  }

  private extractFeedbackData(): FeedBackByCase | null {
    if (
      !this.feedbackDetails.data ||
      !('questionResponses' in this.feedbackDetails.data)
    ) {
      return null;
    }
    return this.feedbackDetails.data as unknown as FeedBackByCase;
  }

  private processCategories(feedbackData: FeedBackByCase): void {
    const categoryKeys = Object.keys(feedbackData.questionResponses);

    for (const categoryKey of categoryKeys) {
      this.processSingleCategory(categoryKey, feedbackData);
    }
  }

  private processSingleCategory(
    categoryKey: string,
    feedbackData: FeedBackByCase
  ): void {
    const mappedCategoryKey = this.getMappedCategoryKey(categoryKey);
    const categoryData = this.getCategoryData(feedbackData, categoryKey);

    const score = this.extractScoreFromCategory(categoryData);
    if (score !== null && mappedCategoryKey in this.overAllScore) {
      (this.overAllScore as Record<string, number>)[mappedCategoryKey] = score;
    }
  }

  private getMappedCategoryKey(categoryKey: string): string {
    const enumKeys = Object.keys(QuestionCategory);
    const foundEnumKey = enumKeys.find(key => key === categoryKey);

    if (foundEnumKey && this.feedbackDetails.mode === InsightMode.RESUME) {
      this.currentStep =
        QuestionCategory[categoryKey as keyof typeof QuestionCategory] - 1;
      return foundEnumKey;
    }
    return categoryKey;
  }

  private getCategoryData(
    feedbackData: FeedBackByCase,
    categoryKey: string
  ): Record<string, unknown> | null {
    const categoryData = (
      feedbackData.questionResponses as Record<string, unknown>
    )[categoryKey];

    if (!categoryData || typeof categoryData !== 'object') {
      return null;
    }
    return categoryData as Record<string, unknown>;
  }

  private extractScoreFromCategory(
    categoryData: Record<string, unknown> | null
  ): number | null {
    if (
      !categoryData ||
      !('overallScore' in categoryData) ||
      typeof (categoryData as { overallScore: number }).overallScore !==
        'number'
    ) {
      return null;
    }
    return (categoryData as { overallScore: number }).overallScore;
  }

  private flattenQuestionResponses(
    feedbackData: FeedBackByCase
  ): QuestionsRes[] {
    if (Array.isArray(feedbackData.questionResponses)) {
      return feedbackData.questionResponses;
    }

    let allResponses: QuestionsRes[] = [];
    Object.keys(feedbackData.questionResponses).forEach(categoryKey => {
      const categoryData = this.getCategoryData(feedbackData, categoryKey);
      const responses = this.extractResponsesFromCategory(categoryData);
      if (responses) {
        allResponses = allResponses.concat(responses);
      }
    });
    return allResponses;
  }

  private extractResponsesFromCategory(
    categoryData: Record<string, unknown> | null
  ): QuestionsRes[] | null {
    if (
      !categoryData ||
      !('questionResponses' in categoryData) ||
      !Array.isArray(
        (categoryData as { questionResponses: QuestionsRes[] })
          .questionResponses
      )
    ) {
      return null;
    }
    return (categoryData as { questionResponses: QuestionsRes[] })
      .questionResponses;
  }

  private mapResponsesToFormFormat(responses: QuestionsRes[]): {
    id: number;
    caseFeedbackQuestionId: number;
    ratingScale: number;
    description: string;
    caseBehaviorMappingId: number | null;
  }[] {
    return responses.map((response: QuestionsRes) => ({
      id: response.id,
      caseFeedbackQuestionId: response.caseFeedbackQuestionId,
      ratingScale: response.ratingScale,
      description: response.answerText || '',
      caseBehaviorMappingId: response.caseBehaviorMappingId || null,
    }));
  }

  private hydrateBehaviorMetricsStateFromMappedResponses(
    mappedResponses: {
      caseFeedbackQuestionId: number;
      ratingScale: number;
      description: string;
      caseBehaviorMappingId: number | null;
    }[],
    noOnservingBehaviorIDs?: string
  ): void {
    const behaviorQuestionIds = new Set(
      this.getBehaviorMetricsQuestions().map(question => question.id)
    );

    const behaviorResponsesById: Record<number, FeedbackStepAnswer[]> = {};

    for (const response of mappedResponses) {
      const behaviorId = response.caseBehaviorMappingId;
      if (
        !behaviorId ||
        !behaviorQuestionIds.has(response.caseFeedbackQuestionId)
      ) {
        continue;
      }

      if (!behaviorResponsesById[behaviorId]) {
        behaviorResponsesById[behaviorId] = [];
      }

      behaviorResponsesById[behaviorId] = behaviorResponsesById[
        behaviorId
      ].filter(
        item => item.caseFeedbackQuestionId !== response.caseFeedbackQuestionId
      );

      behaviorResponsesById[behaviorId].push({
        caseFeedbackQuestionId: response.caseFeedbackQuestionId,
        ratingScale: response.ratingScale,
        description: response.description,
        answerText: response.description,
        caseBehaviorMappingId: behaviorId,
      });
    }

    this.behaviorMetricsResponsesByBehaviorId = behaviorResponsesById;
    this.notObservedBehaviorIds = this.parseNotObservedBehaviorIds(
      noOnservingBehaviorIDs
    );
    this.selectedBehaviorMetricsId =
      this.getInitialBehaviorMetricsId(mappedResponses);
  }

  private parseNotObservedBehaviorIds(
    noObservingBehaviorIDs?: string
  ): number[] {
    if (!noObservingBehaviorIDs?.trim()) {
      return [];
    }

    return Array.from(
      new Set(
        noObservingBehaviorIDs
          .split(',')
          .map(id => Number(id.trim()))
          .filter(id => Number.isFinite(id) && id > 0)
      )
    );
  }

  private getNoObservingBehaviorIdsFromFeedback(
    feedbackData: FeedBackByCase
  ): string {
    return (
      feedbackData.noObservingBehaviorIDs ||
      feedbackData.noOnservingBehaviorIDs ||
      ''
    );
  }

  private getInitialBehaviorMetricsId(
    mappedResponses: {
      caseFeedbackQuestionId: number;
      ratingScale: number;
      description: string;
      caseBehaviorMappingId: number | null;
    }[]
  ): number | null {
    const validBehaviorIds = this.getValidCaseBehaviors().map(
      behavior => behavior.id
    );

    const behaviorQuestionIds = new Set(
      this.getBehaviorMetricsQuestions().map(question => question.id)
    );

    const populatedBehaviorResponse = mappedResponses.find(
      response =>
        !!response.caseBehaviorMappingId &&
        validBehaviorIds.includes(response.caseBehaviorMappingId) &&
        behaviorQuestionIds.has(response.caseFeedbackQuestionId) &&
        (response.ratingScale > 0 || !!response.description?.trim())
    );

    if (populatedBehaviorResponse?.caseBehaviorMappingId) {
      return populatedBehaviorResponse.caseBehaviorMappingId;
    }

    const anyBehaviorResponse = mappedResponses.find(
      response =>
        !!response.caseBehaviorMappingId &&
        validBehaviorIds.includes(response.caseBehaviorMappingId) &&
        behaviorQuestionIds.has(response.caseFeedbackQuestionId)
    );

    if (anyBehaviorResponse?.caseBehaviorMappingId) {
      return anyBehaviorResponse.caseBehaviorMappingId;
    }

    return validBehaviorIds[0] || null;
  }

  private patchFormWithFeedbackData(
    feedbackData: FeedBackByCase,
    mappedResponses: {
      caseFeedbackQuestionId: number;
      ratingScale: number;
      description: string;
      caseBehaviorMappingId: number | null;
    }[]
  ): void {
    this.feedbackForm.patchValue({
      id: feedbackData.id,
      caseId: feedbackData.caseId,
      organizationId: feedbackData.organizationId,
      caseBehaviorMappingId: this.getInitialBehaviorMetricsId(mappedResponses),
    });
  }

  private buildQuestionResponsesArray(
    mappedResponses: {
      caseFeedbackQuestionId: number;
      ratingScale: number;
      description: string;
      caseBehaviorMappingId: number | null;
    }[]
  ): void {
    const questionResponsesArray = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    questionResponsesArray.clear();

    if (!this.questions || this.questions.length === 0) {
      const feedbackData = this.extractFeedbackData();
      if (feedbackData) {
        this.pendingFeedbackData = feedbackData;
      }
      return;
    }

    this.questions.forEach(question => {
      const existingResponse = this.getInitialResponseForQuestion(
        question.id,
        mappedResponses
      );

      const responseGroup = this.fb.group({
        caseFeedbackQuestionId: [question.id],
        ratingScale: [existingResponse?.ratingScale || 0],
        description: [existingResponse?.description || ''],
        caseBehaviorMappingId: [
          existingResponse?.caseBehaviorMappingId || null,
        ],
      });
      questionResponsesArray.push(responseGroup);
    });

    this.toggleDisable();
    this.updateStepDataWithFormReferences();
  }

  private getInitialResponseForQuestion(
    questionId: number,
    mappedResponses: {
      caseFeedbackQuestionId: number;
      ratingScale: number;
      description: string;
      caseBehaviorMappingId: number | null;
    }[]
  ): {
    caseFeedbackQuestionId: number;
    ratingScale: number;
    description: string;
    caseBehaviorMappingId: number | null;
  } | null {
    const isBehaviorMetricsQuestion = this.getBehaviorMetricsQuestions().some(
      question => question.id === questionId
    );

    if (!isBehaviorMetricsQuestion) {
      return (
        mappedResponses.find(
          response => response.caseFeedbackQuestionId === questionId
        ) || null
      );
    }

    const targetBehaviorId =
      this.selectedBehaviorMetricsId || this.getValidCaseBehaviors()[0]?.id;

    if (targetBehaviorId) {
      const matchedResponse = mappedResponses.find(
        response =>
          response.caseFeedbackQuestionId === questionId &&
          response.caseBehaviorMappingId === targetBehaviorId
      );

      if (matchedResponse) {
        return matchedResponse;
      }
    }

    return (
      mappedResponses.find(
        response =>
          response.caseFeedbackQuestionId === questionId &&
          !!response.caseBehaviorMappingId
      ) ||
      mappedResponses.find(
        response => response.caseFeedbackQuestionId === questionId
      ) ||
      null
    );
  }

  updateStepDataWithFormReferences(): void {
    const formArray = this.feedbackForm.get('questionResponses') as FormArray;

    Object.keys(this.stepData).forEach(stepKey => {
      const stepIndex = Number(stepKey);
      const step = this.stepData[stepIndex];
      if (step) {
        step.formArray = formArray;
        const categoryKey = this.getCategoryKeyByStep(stepIndex);
        if (
          categoryKey &&
          this.overAllScore[categoryKey as keyof typeof this.overAllScore]
        ) {
          step.overallScore =
            this.overAllScore[categoryKey as keyof typeof this.overAllScore];
        }
      }
    });
  }

  getCategoryKeyByStep(stepIndex: number): string | null {
    const mappings = [
      { step: 0, key: 'GeneralBehaviourRating' },
      { step: 1, key: 'BehaviorSpecificMetrics' },
      { step: 2, key: 'InterventionAndSupportFeedback' },
      { step: 3, key: 'StakeholderFeedback' },
      { step: 4, key: 'SelfAssessment' },
    ];

    const mapping = mappings.find(m => m.step === stepIndex);
    return mapping ? mapping.key : null;
  }

  getFilteredQuestionsForTab(tabIndex: number): EnhancedFeedbackQuestion[] {
    const currentStepData = this.getCurrentStepData();
    if (!currentStepData?.questions || this.currentStep !== 4) {
      return [];
    }

    let filteredQuestions: FeedbackQuestion[] = [];

    switch (tabIndex) {
      case 0:
        filteredQuestions = currentStepData.questions.filter(
          q => q.subCategoryId === SubQuestionCategory.TeacherReflection
        );
        break;
      case 1:
        filteredQuestions = currentStepData.questions.filter(
          q => q.subCategoryId === SubQuestionCategory.StudentSelfAssessment
        );
        break;
      case 2:
        filteredQuestions = currentStepData.questions.filter(
          q =>
            q.subCategoryId === SubQuestionCategory.TeacherReflection ||
            q.subCategoryId === SubQuestionCategory.StudentSelfAssessment
        );
        break;
      default:
        filteredQuestions = currentStepData.questions;
    }

    return filteredQuestions.map(question => ({
      ...question,
      questionType: this.getQuestionType(question),
      scaleOptions: ['Never', 'Sometimes', 'Often', 'Almost Always'],
    }));
  }

  toggleDisable() {
    const questionArray = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    if (this.isViewMode) {
      questionArray.disable();
    } else {
      questionArray.enable();
    }
  }
  enableEdit() {
    this.feedbackDetails.mode = InsightMode.EDIT;
    this.toggleDisable();
  }

  get isViewMode(): boolean {
    return this.feedbackDetails.mode === InsightMode.VIEW;
  }
  getQuestions(): void {
    this.loaderService.show();
    this.feedbackService
      .getFeedbackQuestionsList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<FeedbackQuestion[]>) => {
          if (response.result) {
            this.questions = response.result;
            const questionsByCategory = this.questions.reduce(
              (acc, question) => {
                const category = question.category;
                if (!acc[category]) {
                  acc[category] = [];
                }
                acc[category].push(question);
                return acc;
              },
              {} as Record<string, FeedbackQuestion[]>
            );

            this.createStepData(questionsByCategory);
            this.buildForm();
            this.getDrawerParams();
          }
        },
        error: error => {
          console.error('Error fetching feedback questions:', error);
          this.commonService.notify(
            'error',
            'Failed to load feedback questions. Using cached questions.'
          );
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  refreshQuestions(): void {
    this.getQuestions();
  }

  createStepData(
    questionsByCategory: Record<string, FeedbackQuestion[]>
  ): void {
    const categoryMappings = [
      {
        key: 'GeneralBehaviourRating',
        title: 'General Behavior Rating',
        step: 0,
      },
      {
        key: 'BehaviorSpecificMetrics',
        title: 'Behavior-Specific Metrics',
        step: 1,
      },
      {
        key: 'InterventionAndSupportFeedback',
        title: 'Intervention & Support Feedback',
        step: 2,
      },
      { key: 'StakeholderFeedback', title: 'Stakeholder Feedback', step: 3 },
      { key: 'SelfAssessment', title: 'Self-Assessment', step: 4 },
    ];

    categoryMappings.forEach(mapping => {
      const questions = questionsByCategory[mapping.key] || [];
      this.stepData[mapping.step] = {
        questions,
        formArray: this.fb.array([]) as FormArray,
        isViewMode: this.isViewMode,
        categoryTitle: mapping.title,
        overallScore: 15,
      };
    });

    this.isStepDataInitialized = true;
  }

  buildForm(): void {
    const formArray = this.fb.array(
      this.questions.map(question => {
        const questionGroup = this.fb.group({
          caseFeedbackQuestionId: [question.id, Validators.required],
          ratingScale: [0, Validators.required],
          description: [''],
          caseBehaviorMappingId: [null],
        });
        return questionGroup;
      })
    );

    this.feedbackForm.setControl('questionResponses', formArray);
    this.feedbackForm.patchValue({
      organizationId: this.feedbackDetails.student?.organizationId || 0,
      caseId: this.feedbackDetails.caseId || 0,
    });
    Object.keys(this.stepData).forEach(stepKey => {
      const stepIndex = Number(stepKey);
      this.stepData[stepIndex].formArray = formArray;
    });

    if (this.pendingFeedbackData) {
      this.processPendingFeedbackData();
    }
  }

  processPendingFeedbackData(): void {
    if (!this.pendingFeedbackData) return;

    const feedbackData = this.pendingFeedbackData;
    let allQuestionResponses: QuestionsRes[] = [];
    if (Array.isArray(feedbackData.questionResponses)) {
      allQuestionResponses = feedbackData.questionResponses;
    } else {
      Object.keys(feedbackData.questionResponses).forEach(categoryKey => {
        const categoryData = (
          feedbackData.questionResponses as Record<string, unknown>
        )[categoryKey];
        if (
          categoryData &&
          typeof categoryData === 'object' &&
          'overallScore' in categoryData &&
          typeof (categoryData as { overallScore: number }).overallScore ===
            'number'
        ) {
          const score = (categoryData as { overallScore: number }).overallScore;
          if (categoryKey in this.overAllScore) {
            (this.overAllScore as Record<string, number>)[categoryKey] = score;
          }
        }
        if (
          categoryData &&
          typeof categoryData === 'object' &&
          'questionResponses' in categoryData &&
          Array.isArray(
            (categoryData as { questionResponses: QuestionsRes[] })
              .questionResponses
          )
        ) {
          allQuestionResponses = allQuestionResponses.concat(
            (categoryData as { questionResponses: QuestionsRes[] })
              .questionResponses
          );
        }
      });
    }

    const mappedResponses = allQuestionResponses.map(
      (response: QuestionsRes) => ({
        id: response.id,
        caseFeedbackQuestionId: response.caseFeedbackQuestionId,
        ratingScale: response.ratingScale,
        description: response.answerText || '',
        caseBehaviorMappingId: response.caseBehaviorMappingId || null,
      })
    );
    this.hydrateBehaviorMetricsStateFromMappedResponses(
      mappedResponses,
      this.getNoObservingBehaviorIdsFromFeedback(feedbackData)
    );
    this.patchFormWithFeedbackData(feedbackData, mappedResponses);
    this.buildQuestionResponsesArray(mappedResponses);
    if (
      feedbackData.questionResponses &&
      !Array.isArray(feedbackData.questionResponses)
    ) {
      this.totalOverAllScore = feedbackData.overallScore || 0;
    }

    this.pendingFeedbackData = null;

    this.toggleDisable();
  }

  private initializeBehaviorMetricsState(): void {
    const caseBehaviors = this.getValidCaseBehaviors();
    this.behaviorMetricsBehaviorIds = caseBehaviors.map(
      behavior => behavior.id
    );

    if (!this.behaviorMetricsBehaviorIds.length) {
      this.selectedBehaviorMetricsId = null;
      this.notObservedBehaviorIds = [];
      this.behaviorMetricsResponsesByBehaviorId = {};
      return;
    }

    // Always select 1st tab initially for VIEW and EDIT modes
    this.selectedBehaviorMetricsId = this.behaviorMetricsBehaviorIds[0];

    this.feedbackForm.patchValue({
      caseBehaviorMappingId: this.selectedBehaviorMetricsId,
    });

    this.applyBehaviorMetricsResponsesToForm(this.selectedBehaviorMetricsId);
  }

  onBehaviorMetricsTabChanged(behaviorId: number): void {
    if (
      !behaviorId ||
      this.selectedBehaviorMetricsId === behaviorId ||
      !this.behaviorMetricsBehaviorIds.includes(behaviorId)
    ) {
      return;
    }

    this.persistCurrentBehaviorMetricsResponses();
    this.selectedBehaviorMetricsId = behaviorId;
    this.feedbackForm.patchValue({
      caseBehaviorMappingId: behaviorId,
    });
    this.applyBehaviorMetricsResponsesToForm(behaviorId);
  }

  onBehaviorMetricsNotObservedChanged(event: {
    behaviorId: number;
    isNotObserved: boolean;
  }): void {
    const { behaviorId, isNotObserved } = event;
    if (!this.behaviorMetricsBehaviorIds.includes(behaviorId)) {
      return;
    }

    const notObservedSet = new Set(this.notObservedBehaviorIds);
    if (isNotObserved) {
      notObservedSet.add(behaviorId);
    } else {
      notObservedSet.delete(behaviorId);
    }

    this.notObservedBehaviorIds = Array.from(notObservedSet);

    if (isNotObserved) {
      this.behaviorMetricsResponsesByBehaviorId[behaviorId] = [];
      this.applyBehaviorMetricsResponsesToForm(behaviorId);
      return;
    }

    this.applyBehaviorMetricsResponsesToForm(behaviorId);
  }

  private seedBehaviorMetricsResponsesFromCurrentForm(): void {
    const behaviorQuestions = this.getBehaviorMetricsQuestions();
    if (!behaviorQuestions.length) {
      return;
    }

    const formResponses =
      (this.feedbackForm.value.questionResponses as FeedbackStepAnswer[]) || [];

    for (const response of formResponses) {
      const isBehaviorQuestion = behaviorQuestions.some(
        question => question.id === response.caseFeedbackQuestionId
      );
      const behaviorId = response.caseBehaviorMappingId || 0;

      if (!isBehaviorQuestion || !behaviorId) {
        continue;
      }

      if (!this.behaviorMetricsResponsesByBehaviorId[behaviorId]) {
        this.behaviorMetricsResponsesByBehaviorId[behaviorId] = [];
      }

      this.behaviorMetricsResponsesByBehaviorId[behaviorId] =
        this.behaviorMetricsResponsesByBehaviorId[behaviorId].filter(
          item =>
            item.caseFeedbackQuestionId !== response.caseFeedbackQuestionId
        );

      this.behaviorMetricsResponsesByBehaviorId[behaviorId].push({
        caseFeedbackQuestionId: response.caseFeedbackQuestionId,
        ratingScale: response.ratingScale,
        description: response.description || '',
        answerText: response.answerText || response.description || '',
        caseBehaviorMappingId: behaviorId,
      });
    }

    this.initializeBehaviorMetricsState();
  }

  private getValidCaseBehaviors(): CaseBehaviour[] {
    return (
      this.feedbackDetails.caseDetail?.caseBehaviors?.filter(
        behavior => !behavior.draft && behavior.statusId !== 2
      ) || []
    );
  }

  private getBehaviorMetricsQuestions(): FeedbackQuestion[] {
    return this.stepData[1]?.questions || [];
  }

  private persistCurrentBehaviorMetricsResponses(): void {
    const behaviorId = this.selectedBehaviorMetricsId;
    if (!behaviorId) {
      return;
    }

    if (this.notObservedBehaviorIds.includes(behaviorId)) {
      this.behaviorMetricsResponsesByBehaviorId[behaviorId] = [];
      return;
    }

    this.behaviorMetricsResponsesByBehaviorId[behaviorId] =
      this.buildCurrentBehaviorMetricsResponsesFromForm(behaviorId);
  }

  private buildCurrentBehaviorMetricsResponsesFromForm(
    behaviorId: number
  ): FeedbackStepAnswer[] {
    const behaviorQuestions = this.getBehaviorMetricsQuestions();
    const responsesArray = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;

    return behaviorQuestions
      .map(question => {
        const index = this.getQuestionIndex(question.id);
        if (index < 0) {
          return null;
        }

        const control = responsesArray.at(index);
        return {
          caseFeedbackQuestionId: question.id,
          ratingScale: Number(control.get('ratingScale')?.value || 0),
          description: String(control.get('description')?.value || ''),
          answerText: String(control.get('description')?.value || ''),
          caseBehaviorMappingId: behaviorId,
        } as FeedbackStepAnswer;
      })
      .filter((response): response is FeedbackStepAnswer => response !== null);
  }

  private applyBehaviorMetricsResponsesToForm(behaviorId: number): void {
    const behaviorQuestions = this.getBehaviorMetricsQuestions();
    const responsesArray = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    const storedResponses =
      this.behaviorMetricsResponsesByBehaviorId[behaviorId] || [];

    for (const question of behaviorQuestions) {
      const questionIndex = this.getQuestionIndex(question.id);
      if (questionIndex < 0) {
        continue;
      }

      const matchingStoredResponse = storedResponses.find(
        response => response.caseFeedbackQuestionId === question.id
      );

      const control = responsesArray.at(questionIndex);
      control.patchValue({
        ratingScale: matchingStoredResponse?.ratingScale || 0,
        description: matchingStoredResponse?.description || '',
        caseBehaviorMappingId: behaviorId,
      });
    }
  }

  private buildBehaviorMetricsQuestionResponsesPayload(): FeedbackStepAnswer[] {
    const payloadResponses: FeedbackStepAnswer[] = [];
    const behaviorQuestions = this.getBehaviorMetricsQuestions();

    for (const behaviorId of this.behaviorMetricsBehaviorIds) {
      if (this.notObservedBehaviorIds.includes(behaviorId)) {
        continue;
      }

      const storedResponses =
        this.behaviorMetricsResponsesByBehaviorId[behaviorId] || [];

      for (const question of behaviorQuestions) {
        const response = storedResponses.find(
          item => item.caseFeedbackQuestionId === question.id
        );

        payloadResponses.push({
          caseFeedbackQuestionId: question.id,
          ratingScale: response?.ratingScale || 0,
          description: response?.description || '',
          answerText: response?.description || '',
          caseBehaviorMappingId: behaviorId,
        });
      }
    }

    return payloadResponses;
  }

  private buildNoOnservingBehaviorIdsValue(): string {
    if (!this.notObservedBehaviorIds.length) {
      return '';
    }

    return this.notObservedBehaviorIds.join(',');
  }

  private isBehaviorMetricsResponseComplete(behaviorId: number): boolean {
    if (this.notObservedBehaviorIds.includes(behaviorId)) {
      return true;
    }

    const storedResponses =
      this.behaviorMetricsResponsesByBehaviorId[behaviorId];
    if (!storedResponses?.length) {
      return false;
    }

    const behaviorQuestions = this.getBehaviorMetricsQuestions();
    return behaviorQuestions.every(question => {
      const questionType = this.getQuestionType(question);
      const response = storedResponses.find(
        item => item.caseFeedbackQuestionId === question.id
      );

      if (!response) {
        return false;
      }

      if (questionType === 'text') {
        return true;
      }

      return Boolean(response.ratingScale && response.ratingScale > 0);
    });
  }

  private areAllBehaviorsHandled(): boolean {
    return this.behaviorMetricsBehaviorIds.every(behaviorId =>
      this.isBehaviorMetricsResponseComplete(behaviorId)
    );
  }

  private getNextBehaviorMetricsId(): number | null {
    if (!this.selectedBehaviorMetricsId) {
      return null;
    }

    const currentIndex = this.behaviorMetricsBehaviorIds.indexOf(
      this.selectedBehaviorMetricsId
    );

    if (currentIndex < 0) {
      return null;
    }

    return this.behaviorMetricsBehaviorIds[currentIndex + 1] || null;
  }

  private getPreviousBehaviorMetricsId(): number | null {
    if (!this.selectedBehaviorMetricsId) {
      return null;
    }

    const currentIndex = this.behaviorMetricsBehaviorIds.indexOf(
      this.selectedBehaviorMetricsId
    );

    if (currentIndex <= 0) {
      return null;
    }

    return this.behaviorMetricsBehaviorIds[currentIndex - 1] || null;
  }

  submitFeedback(options?: {
    autoAdvanceStep?: boolean;
    onSuccess?: () => void;
  }): void {
    const autoAdvanceStep = options?.autoAdvanceStep ?? true;

    // Filter to only current step questions for submission FIRST
    const formValue = { ...this.feedbackForm.value };
    const currentStepData = this.getCurrentStepData();
    if (currentStepData?.questions) {
      if (this.currentStep === 1) {
        this.persistCurrentBehaviorMetricsResponses();
        formValue.questionResponses =
          this.buildBehaviorMetricsQuestionResponsesPayload();
        formValue.noOnservingBehaviorIDs =
          this.buildNoOnservingBehaviorIdsValue();
      } else {
        const currentStepQuestionIds = new Set(
          currentStepData.questions.map(q => q.id)
        );
        formValue.questionResponses =
          this.feedbackForm.value.questionResponses?.filter(
            (response: FeedbackStepAnswer) =>
              currentStepQuestionIds.has(response.caseFeedbackQuestionId)
          ) || [];
      }
    }

    // Build audit trail ONLY from current step responses
    let _modifiedFields: AuditFieldChange[] = [];
    _modifiedFields = this.buildFeedbackModifiedFields(formValue);

    if (
      formValue.questionResponses &&
      Array.isArray(formValue.questionResponses)
    ) {
      formValue.questionResponses = formValue.questionResponses.map(
        (response: FeedbackStepAnswer) => {
          const { description, ...responseWithoutDescription } = response;
          return {
            ...responseWithoutDescription,
            answerText: description || '',
          };
        }
      );
    }

    const payload = {
      ...formValue,
      ...(_modifiedFields.length > 0 && { _modifiedFields }),
    };

    this.loaderService.show();
    this.feedbackService
      .submitQuestionFeedback(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: response => {
          this.loaderService.hide();
          if (response.result) {
            this.feedbackForm.patchValue({
              caseFeedbackSubmissionId:
                response.result.caseFeedbackSubmissionId,
            });
            this.totalOverAllScore = response.result.overallScore || 0;
          }
          if (this.currentStep === 4) {
            this.commonService.notify(
              'success',
              `Feedback submitted successfully`
            );
            this.drawerService.closeDrawer(DRAWER_IDS.FEEDBACK_FORM);
          }

          options?.onSuccess?.();

          if (autoAdvanceStep && this.currentStep < this.steps.length - 1) {
            this.currentStep++;
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  closeCorrectionNeededDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.FEEDBACK_FORM);
  }

  nextStep(): void {
    if (this.feedbackDetails.mode === InsightMode.VIEW) {
      if (this.currentStep === 1) {
        const nextBehaviorId = this.getNextBehaviorMetricsId();
        if (nextBehaviorId) {
          this.onBehaviorMetricsTabChanged(nextBehaviorId);
          return;
        }
      }

      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    } else {
      if (this.currentStep === 1) {
        this.handleBehaviorMetricsStepNext();
        return;
      }

      this.submitFeedback();
    }
  }

  private handleBehaviorMetricsStepNext(): void {
    const selectedBehaviorId = this.selectedBehaviorMetricsId;
    if (!selectedBehaviorId) {
      return;
    }

    this.persistCurrentBehaviorMetricsResponses();

    if (!this.isBehaviorMetricsResponseComplete(selectedBehaviorId)) {
      this.commonService.notify(
        'error',
        'Please answer all required questions or mark this behavior as not observed.'
      );
      return;
    }

    const nextBehaviorId = this.getNextBehaviorMetricsId();
    if (nextBehaviorId) {
      this.submitFeedback({
        autoAdvanceStep: false,
        onSuccess: () => this.onBehaviorMetricsTabChanged(nextBehaviorId),
      });
      return;
    }

    if (!this.areAllBehaviorsHandled()) {
      this.commonService.notify(
        'error',
        'All behaviors must be completed or marked as not observed before continuing.'
      );
      return;
    }

    this.submitFeedback();
  }

  previousStep(): void {
    if (this.currentStep === 1) {
      const previousBehaviorId = this.getPreviousBehaviorMetricsId();
      if (previousBehaviorId) {
        this.onBehaviorMetricsTabChanged(previousBehaviorId);
        return;
      }
    }

    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step >= 0 && step < this.steps.length) {
      this.currentStep = step;
    }
  }

  getCurrentStepData(): FeedbackStepData | undefined {
    const stepData = this.stepData[this.currentStep];
    return stepData;
  }

  isFirstStep(): boolean {
    return this.currentStep === 0;
  }

  isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  isStepValid(): boolean {
    if (!this.isStepDataInitialized) {
      return false;
    }

    const stepData = this.getCurrentStepData();
    if (!stepData) {
      return false;
    }

    return this.validateCurrentStepForm();
  }

  private validateCurrentStepForm(): boolean {
    const questionResponses = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    const currentStepData = this.getCurrentStepData();

    if (!currentStepData?.questions) {
      return false;
    }

    if (this.currentStep === 1) {
      const selectedBehaviorId = this.selectedBehaviorMetricsId;
      if (!selectedBehaviorId) {
        return false;
      }

      if (this.notObservedBehaviorIds.includes(selectedBehaviorId)) {
        return true;
      }

      return currentStepData.questions.every(question => {
        const questionIndex = this.getQuestionIndex(question.id);
        if (questionIndex < 0) {
          return false;
        }

        const control = questionResponses.at(questionIndex);
        const questionType = this.getQuestionType(question);
        const ratingScale = control.get('ratingScale')?.value;

        if (questionType === 'text') {
          return true;
        }

        return Boolean(ratingScale && ratingScale > 0);
      });
    }
    if (this.currentStep === 4) {
      return true;
    }

    return currentStepData.questions.every(question => {
      const questionIndex = this.getQuestionIndex(question.id);
      if (questionIndex >= 0) {
        const control = questionResponses.at(questionIndex);
        const questionType = this.getQuestionType(question);
        const ratingScale = control.get('ratingScale')?.value;
        const description = control.get('description')?.value;

        if (questionType === 'text') {
          if (this.currentStep === 1) {
            return true;
          }
          return description && description.trim() !== '';
        } else {
          // For currentStep === 2, if ratingScale is 3, description is required
          if (this.currentStep === 2 && ratingScale === 3) {
            return description && description.trim() !== '';
          }
          return ratingScale && ratingScale > 0;
        }
      }
      return false;
    });
  }

  private getQuestionIndex(questionId: number): number {
    const questionResponses = this.feedbackForm.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.controls.findIndex(
      control => control.get('caseFeedbackQuestionId')?.value === questionId
    );
  }

  private getQuestionType(question: FeedbackQuestion): string {
    return QUESTION_TYPE_MAP[question.id] || QuestionType.Scale;
  }

  validateStep(): StepValidation {
    const isValid = this.isStepValid();
    return {
      isValid,
      errors: isValid ? undefined : ['Please complete all required fields.'],
    };
  }

  getStepStatus(stepIndex: number): string {
    if (stepIndex < this.currentStep) {
      return 'finish';
    } else if (stepIndex === this.currentStep) {
      return 'process';
    } else {
      return 'wait';
    }
  }

  async printTemplate() {
    if (this.currentStep === 4) {
      const caseDetail = this.feedbackDetails.caseDetail;
      const selectedTabIndex =
        this.selfAssessmentStepComponent?.selectedTabIndex ?? 0;
      const filteredQuestions =
        this.getFilteredQuestionsForTab(selectedTabIndex);
      const tabNames = ['Teacher', 'Student', 'Both'];
      const factory = this.viewContainerRef.createComponent(
        PrintTemplateComponent,
        {
          injector: this.injector,
        }
      );
      const componentElement = factory.location.nativeElement;
      componentElement.style.display = 'none';
      this.isPrintLoading = true;
      await Promise.all([
        this.orgService
          .getOrganization(caseDetail?.student.organizationId)
          .toPromise(),
      ])
        .then(async ([orgRes]: [OrganizationModel | undefined]) => {
          let base64Image = '';
          if (orgRes?.logoUrl) {
            base64Image = await this.commonService.getBase64ImageFromUrl(
              orgRes.logoUrl
            );
          } else {
            base64Image = await this.commonService.getBase64ImageFromUrl(
              '/assets/images/logo.png'
            );
          }
          factory.instance.templateData = {
            questionList: filteredQuestions,
            logo: base64Image,
            caseDetail: caseDetail,
            orgDetail: orgRes,
            selectedTab: tabNames[selectedTabIndex] || 'Teacher Only',
          } as PrintTemplateData;
          this.cd.detectChanges();
          this.waitForInitialization(factory).then(() => {
            factory.instance.printPDF();
            factory.instance.templateData = null;
            factory.destroy();
          });
        })
        .catch(e => console.error(e))
        .finally(() => {
          this.isPrintLoading = false;
          this.cd.detectChanges();
        });
    } else {
      return;
    }
  }

  waitForInitialization(factory: ComponentRef<PrintTemplateComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  private buildFeedbackModifiedFields(
    formValue: FeedbackAuditFormValue
  ): AuditFieldChange[] {
    const allModifiedFields: AuditFieldChange[] = [];

    this.appendBasicFeedbackFieldChanges(formValue, allModifiedFields);
    this.appendNotObservedBehaviorAuditChange(formValue, allModifiedFields);
    this.appendQuestionResponseAuditChanges(formValue, allModifiedFields);

    return allModifiedFields;
  }

  private appendBasicFeedbackFieldChanges(
    formValue: FeedbackAuditFormValue,
    allModifiedFields: AuditFieldChange[]
  ): void {
    if (this.isViewMode || !this.feedbackDetails?.data) {
      return;
    }

    const original = {
      organizationId: this.feedbackDetails.data.organizationId || 0,
      caseId: this.feedbackDetails.data.caseId || 0,
      caseBehaviorMappingId:
        this.feedbackForm.get('caseBehaviorMappingId')?.value || null,
    };

    const labelMap: Record<string, string> = {
      organizationId: 'School',
      caseId: 'Case ID',
      caseBehaviorMappingId: 'Behavior Category',
    };

    const sensitiveFields: string[] = [];
    const excludeFields = [
      'questionResponses',
      'organizationId',
      'caseId',
      'caseBehaviorMappingId',
      'caseFeedbackSubmissionId',
      'noOnservingBehaviorIDs',
      'noObservingBehaviorIDs',
    ];

    const basicFieldChanges = this.auditContextService.buildFieldChanges(
      original,
      formValue,
      { sensitiveFields, labelMap, excludeFields }
    );

    allModifiedFields.push(...basicFieldChanges);
  }

  private appendNotObservedBehaviorAuditChange(
    formValue: FeedbackAuditFormValue,
    allModifiedFields: AuditFieldChange[]
  ): void {
    const notObservedIdsStr = this.getNotObservedBehaviorIdsForAudit(formValue);
    if (!notObservedIdsStr.trim()) {
      return;
    }

    const notObservedNames =
      this.getNotObservedBehaviorNames(notObservedIdsStr);
    if (!notObservedNames.length) {
      return;
    }

    allModifiedFields.push({
      fieldLabel: 'Behavior Not Observed',
      oldValue: null,
      newValue: `${notObservedNames}`,
      isSensitive: false,
    });
  }

  private getNotObservedBehaviorIdsForAudit(
    formValue: FeedbackAuditFormValue
  ): string {
    const value = formValue['noOnservingBehaviorIDs'];
    return typeof value === 'string' ? value : '';
  }

  private getNotObservedBehaviorNames(notObservedIdsStr: string): string[] {
    const notObservedIds = notObservedIdsStr
      .split(',')
      .map(id => Number(id.trim()))
      .filter(id => Number.isFinite(id) && id > 0);

    const caseBehaviors = this.feedbackDetails.caseDetail?.caseBehaviors || [];
    return notObservedIds
      .map(id => {
        const found = caseBehaviors.find(b => b.id === id);
        return found
          ? found.behaviorCategoryName || `Behavior #${id}`
          : `Behavior #${id}`;
      })
      .filter(Boolean);
  }

  private appendQuestionResponseAuditChanges(
    formValue: FeedbackAuditFormValue,
    allModifiedFields: AuditFieldChange[]
  ): void {
    const currentStepData = this.getCurrentStepData();
    const responses = formValue['questionResponses'];

    if (!Array.isArray(responses) || !currentStepData?.questions) {
      return;
    }

    const originalResponses = this.getOriginalQuestionResponses();
    const currentStepQuestionIds = new Set(
      currentStepData.questions.map(q => q.id)
    );
    const selectedBehaviorId = this.selectedBehaviorMetricsId;
    const responsesToAudit = this.getResponsesToAudit(
      responses,
      selectedBehaviorId
    );

    for (const response of responsesToAudit) {
      if (!currentStepQuestionIds.has(response.caseFeedbackQuestionId)) {
        continue;
      }

      const originalResponse = this.findOriginalResponseForAudit(
        response,
        originalResponses
      );
      const questionText = this.buildAuditQuestionText(
        response,
        selectedBehaviorId
      );

      this.appendRatingAuditChange(
        response,
        originalResponse,
        questionText,
        allModifiedFields
      );
      this.appendAnswerAuditChange(
        response,
        originalResponse,
        questionText,
        allModifiedFields
      );
      this.appendBehaviorCategoryAuditChange(
        response,
        originalResponse,
        questionText,
        allModifiedFields
      );
    }
  }

  private getResponsesToAudit(
    responses: FeedbackStepAnswer[],
    selectedBehaviorId: number | null
  ): FeedbackStepAnswer[] {
    if (this.currentStep !== 1 || !selectedBehaviorId) {
      return responses;
    }

    return responses.filter(
      response => (response.caseBehaviorMappingId || 0) === selectedBehaviorId
    );
  }

  private findOriginalResponseForAudit(
    response: FeedbackStepAnswer,
    originalResponses: FeedbackStepAnswer[]
  ): FeedbackStepAnswer | undefined {
    return originalResponses.find(
      original =>
        original.caseFeedbackQuestionId === response.caseFeedbackQuestionId &&
        (original.caseBehaviorMappingId ||
          original.behaviourCategoryId ||
          0) ===
          (response.caseBehaviorMappingId || response.behaviourCategoryId || 0)
    );
  }

  private buildAuditQuestionText(
    response: FeedbackStepAnswer,
    selectedBehaviorId: number | null
  ): string {
    const question = this.questions.find(
      q => q.id === response.caseFeedbackQuestionId
    );
    const baseQuestionText = question
      ? question.question
      : `Question ${response.caseFeedbackQuestionId}`;

    const isBehaviorQuestion =
      this.currentStep === 1 || !!response.caseBehaviorMappingId;
    if (!isBehaviorQuestion) {
      return baseQuestionText;
    }

    const behaviorIdForLabel =
      response.caseBehaviorMappingId || selectedBehaviorId || null;
    return `${this.getBehaviorCategoryNameForAudit(behaviorIdForLabel)} ${baseQuestionText}`.trim();
  }

  private getBehaviorCategoryNameForAudit(
    behaviorId: number | null | undefined
  ): string {
    if (!behaviorId) {
      return '';
    }

    const caseBehaviors = this.feedbackDetails.caseDetail?.caseBehaviors || [];
    const found = caseBehaviors.find(b => b.id === behaviorId);
    return found ? ` (${found.behaviorCategoryName || 'Behavior'})` : '';
  }

  private appendRatingAuditChange(
    response: FeedbackStepAnswer,
    originalResponse: FeedbackStepAnswer | undefined,
    questionText: string,
    allModifiedFields: AuditFieldChange[]
  ): void {
    if (originalResponse?.ratingScale === response.ratingScale) {
      return;
    }

    allModifiedFields.push({
      fieldLabel: `${questionText} - Rating`,
      oldValue: originalResponse
        ? this.getFormattedRatingForAudit(
            response.caseFeedbackQuestionId,
            originalResponse.ratingScale
          )
        : null,
      newValue: this.getFormattedRatingForAudit(
        response.caseFeedbackQuestionId,
        response.ratingScale
      ),
      isSensitive: false,
    });
  }

  private getFormattedRatingForAudit(
    questionId: number,
    ratingValue: number
  ): string {
    const defaultLabels: Record<number, string> = {
      1: 'Never',
      2: 'Rarely',
      3: 'Sometimes',
      4: 'Often',
      5: 'Always',
    };

    if (ratingValue === 0) {
      return '';
    }

    const ratingLabelMap = QUESTION_SCALE_LABELS[questionId] || defaultLabels;
    const label = ratingLabelMap[ratingValue] || 'Unknown';
    return `${ratingValue} (${label})`;
  }

  private appendAnswerAuditChange(
    response: FeedbackStepAnswer,
    originalResponse: FeedbackStepAnswer | undefined,
    questionText: string,
    allModifiedFields: AuditFieldChange[]
  ): void {
    const newAnswerText = response.description || '';
    const oldAnswerText = originalResponse?.answerText || '';

    if (newAnswerText === oldAnswerText) {
      return;
    }

    allModifiedFields.push({
      fieldLabel: `${questionText} - Answer`,
      oldValue: oldAnswerText.trim() ? oldAnswerText : null,
      newValue: newAnswerText.trim() ? newAnswerText : null,
      isSensitive: false,
    });
  }

  private appendBehaviorCategoryAuditChange(
    response: FeedbackStepAnswer,
    originalResponse: FeedbackStepAnswer | undefined,
    questionText: string,
    allModifiedFields: AuditFieldChange[]
  ): void {
    if (
      response.behaviourCategoryId === undefined ||
      originalResponse?.behaviourCategoryId === response.behaviourCategoryId
    ) {
      return;
    }

    allModifiedFields.push({
      fieldLabel: `${questionText} - Behavior Category`,
      oldValue: originalResponse?.behaviourCategoryId ?? null,
      newValue: response.behaviourCategoryId,
      isSensitive: false,
    });
  }

  private getOriginalQuestionResponses(): FeedbackStepAnswer[] {
    if (!this.feedbackDetails.data) {
      return [];
    }

    const feedbackData = this.extractFeedbackData();
    if (!feedbackData?.questionResponses) {
      return [];
    }

    const allQuestionResponses = this.flattenQuestionResponses(feedbackData);
    return allQuestionResponses.map((response: QuestionsRes) => ({
      caseFeedbackQuestionId: response.caseFeedbackQuestionId,
      ratingScale: response.ratingScale,
      answerText: response.answerText || '',
      caseBehaviorMappingId: response.caseBehaviorMappingId || 0,
      behaviourCategoryId: response.caseBehaviorMappingId || 0,
    }));
  }
}
